"use client"
// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

/**
 * Public guide (ADR-027 rule 8): /guides/why-the-scale-lies. Tells the
 * "the daily number is noise, the trend is signal" story that mirrors
 * Amy's PROGRESS AND THE SCALE system-prompt section
 * (patterns/bitebalance-agent/system_prompt.py) — the myth of the daily
 * weigh-in, what actually moves the scale that isn't fat, why BiteBalance
 * watches 7-day trends and calibration windows instead of single
 * weigh-ins, what actually signals progress, and how Amy handles a flat
 * week without blame.
 *
 * Same standalone-route pattern as SaveYourDayPage.tsx: no auth, no
 * personal context, no dependency on the landing page's components. Copy
 * lives in packages/i18n's guides.en.ts/guides.de.ts under the
 * `guides.whyTheScaleLies` key, merged into the shared t() dictionary the
 * same way `guides.saveYourDay` is.
 */

import { useEffect } from "react"
import { Link } from "react-router-dom"
import { Button } from "@/components/ui/button"
import { LanguageToggle } from "@/components/layout/LanguageToggle"
import { useI18n } from "@/i18n"
import { PageEntrance, ReadingProgress } from "@/components/PageEntrance"

export default function WhyTheScaleLiesPage() {
  const { t } = useI18n()

  useEffect(() => {
    const previousTitle = document.title
    document.title = t("guides.whyTheScaleLies.meta.title")

    const meta = document.querySelector('meta[name="description"]')
    const previousDescription = meta?.getAttribute("content") ?? null
    meta?.setAttribute("content", t("guides.whyTheScaleLies.meta.description"))

    return () => {
      document.title = previousTitle
      if (previousDescription !== null) meta?.setAttribute("content", previousDescription)
    }
  }, [t])

  const moveItems = [
    {
      title: t("guides.whyTheScaleLies.moves.item1Title"),
      body: t("guides.whyTheScaleLies.moves.item1Body"),
    },
    {
      title: t("guides.whyTheScaleLies.moves.item2Title"),
      body: t("guides.whyTheScaleLies.moves.item2Body"),
    },
    {
      title: t("guides.whyTheScaleLies.moves.item3Title"),
      body: t("guides.whyTheScaleLies.moves.item3Body"),
    },
    {
      title: t("guides.whyTheScaleLies.moves.item4Title"),
      body: t("guides.whyTheScaleLies.moves.item4Body"),
    },
    {
      title: t("guides.whyTheScaleLies.moves.item5Title"),
      body: t("guides.whyTheScaleLies.moves.item5Body"),
    },
  ]

  const signalItems = [
    {
      title: t("guides.whyTheScaleLies.signals.item1Title"),
      body: t("guides.whyTheScaleLies.signals.item1Body"),
    },
    {
      title: t("guides.whyTheScaleLies.signals.item2Title"),
      body: t("guides.whyTheScaleLies.signals.item2Body"),
    },
    {
      title: t("guides.whyTheScaleLies.signals.item3Title"),
      body: t("guides.whyTheScaleLies.signals.item3Body"),
    },
  ]

  return (
    <PageEntrance>
      <ReadingProgress />
      <div className="min-h-screen bg-cream text-ink">
        <a
          href="#main"
          className="sr-only focus-visible:not-sr-only focus-visible:fixed focus-visible:top-2 focus-visible:left-2 focus-visible:z-50 focus-visible:rounded-bb-md focus-visible:bg-paper focus-visible:px-4 focus-visible:py-2 focus-visible:text-ink focus-visible:shadow-[var(--bb-shadow)]"
        >
          {t("nav.skipToContent")}
        </a>

        <header className="border-b border-line bg-paper">
          <div className="mx-auto flex max-w-3xl items-center justify-between gap-4 px-4 py-4 md:px-8">
            <Link to="/" className="text-sm font-semibold text-ink-2 hover:text-ink">
              ← {t("guides.whyTheScaleLies.backToHome")}
            </Link>
            <LanguageToggle />
          </div>
        </header>

        <main id="main">
          <article className="mx-auto max-w-3xl px-4 py-14 md:px-8 md:py-20">
            {/* Hero */}
            <p className="text-sm font-semibold tracking-wide text-green uppercase">
              {t("guides.whyTheScaleLies.eyebrow")}
            </p>
            <h1 className="mt-3 text-4xl leading-tight font-black text-ink md:text-5xl">
              {t("guides.whyTheScaleLies.hero.title")}
            </h1>
            <p className="mt-5 max-w-prose text-lg text-ink-2">
              {t("guides.whyTheScaleLies.hero.subtitle")}
            </p>
            <p className="mt-3 text-sm font-medium text-ink-2">
              {t("guides.whyTheScaleLies.hero.readTime")}
            </p>

            {/* The myth of the daily weigh-in */}
            <section className="mt-12">
              <h2 className="text-2xl font-bold text-ink md:text-3xl">
                {t("guides.whyTheScaleLies.hook.title")}
              </h2>
              <p className="mt-4 text-base leading-relaxed text-ink-2">
                {t("guides.whyTheScaleLies.hook.body1")}
              </p>
              <p className="mt-4 text-base leading-relaxed text-ink-2">
                {t("guides.whyTheScaleLies.hook.body2")}
              </p>
              <blockquote className="mt-6 rounded-bb-lg border-l-4 border-green bg-paper p-5 text-lg font-medium text-ink shadow-[var(--bb-shadow)]">
                {t("guides.whyTheScaleLies.hook.quoteBody")}
              </blockquote>
            </section>

            {/* What moves the scale that isn't fat */}
            <section className="mt-14 rounded-bb-xl border border-line bg-paper p-6 shadow-[var(--bb-shadow)] md:p-8">
              <h2 className="text-2xl font-bold text-ink md:text-3xl">
                {t("guides.whyTheScaleLies.moves.title")}
              </h2>
              <p className="mt-3 text-base text-ink-2">{t("guides.whyTheScaleLies.moves.intro")}</p>

              <ul className="mt-6 flex flex-col gap-6">
                {moveItems.map(item => (
                  <li key={item.title}>
                    <h3 className="text-lg font-semibold text-ink">{item.title}</h3>
                    <p className="mt-1 text-base text-ink-2">{item.body}</p>
                  </li>
                ))}
              </ul>
            </section>

            {/* Why BiteBalance watches trends, not days */}
            <section className="mt-14">
              <h2 className="text-2xl font-bold text-ink md:text-3xl">
                {t("guides.whyTheScaleLies.trends.title")}
              </h2>
              <p className="mt-4 text-base leading-relaxed text-ink-2">
                {t("guides.whyTheScaleLies.trends.body1")}
              </p>
              <p className="mt-4 text-base leading-relaxed text-ink-2">
                {t("guides.whyTheScaleLies.trends.body2")}
              </p>

              <h3 className="mt-6 text-lg font-semibold text-ink">
                {t("guides.whyTheScaleLies.trends.listTitle")}
              </h3>
              <ul className="mt-3 flex flex-col gap-2">
                {[
                  t("guides.whyTheScaleLies.trends.listItem1"),
                  t("guides.whyTheScaleLies.trends.listItem2"),
                  t("guides.whyTheScaleLies.trends.listItem3"),
                  t("guides.whyTheScaleLies.trends.listItem4"),
                ].map(item => (
                  <li key={item} className="flex gap-3 text-base text-ink-2">
                    <span aria-hidden="true" className="mt-1 shrink-0 text-green">
                      ✓
                    </span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </section>

            {/* What actually signals progress */}
            <section className="mt-14">
              <h2 className="text-2xl font-bold text-ink md:text-3xl">
                {t("guides.whyTheScaleLies.signals.title")}
              </h2>
              <p className="mt-4 text-base leading-relaxed text-ink-2">
                {t("guides.whyTheScaleLies.signals.intro")}
              </p>
              <div className="mt-6 grid gap-4 md:grid-cols-3">
                {signalItems.map(item => (
                  <div
                    key={item.title}
                    className="rounded-bb-lg border border-line bg-paper p-5 shadow-[var(--bb-shadow)]"
                  >
                    <h3 className="text-base font-semibold text-ink">{item.title}</h3>
                    <p className="mt-2 text-sm text-ink-2">{item.body}</p>
                  </div>
                ))}
              </div>
            </section>

            {/* How Amy handles a flat week */}
            <section className="mt-14 rounded-bb-xl border border-line bg-paper p-6 shadow-[var(--bb-shadow)] md:p-8">
              <h2 className="text-2xl font-bold text-ink md:text-3xl">
                {t("guides.whyTheScaleLies.amyHandles.title")}
              </h2>
              <p className="mt-4 text-base leading-relaxed text-ink-2">
                {t("guides.whyTheScaleLies.amyHandles.body1")}
              </p>
              <p className="mt-4 text-base leading-relaxed text-ink-2">
                {t("guides.whyTheScaleLies.amyHandles.body2")}
              </p>
              <p className="mt-4 text-base font-semibold text-ink">
                {t("guides.whyTheScaleLies.amyHandles.body3")}
              </p>

              <p className="mt-6 text-sm text-ink-2">
                {t("guides.whyTheScaleLies.relatedGuide.label")}:{" "}
                <Link
                  to="/guides/save-your-day"
                  className="font-semibold text-green underline underline-offset-2"
                >
                  {t("guides.whyTheScaleLies.relatedGuide.linkText")}
                </Link>
              </p>
            </section>

            {/* CTA */}
            <section className="mt-16 rounded-bb-xl border border-line bg-paper p-8 text-center shadow-[var(--bb-shadow)]">
              <h2 className="text-2xl font-bold text-ink md:text-3xl">
                {t("guides.whyTheScaleLies.cta.title")}
              </h2>
              <p className="mx-auto mt-3 max-w-prose text-base text-ink-2">
                {t("guides.whyTheScaleLies.cta.body")}
              </p>
              <Button type="button" size="lg" className="mt-6 min-h-11" asChild>
                <Link to="/">
                  {t("guides.whyTheScaleLies.cta.button")}
                  <span aria-hidden="true">→</span>
                </Link>
              </Button>
            </section>

            {/* Disclaimer */}
            <p className="mt-10 text-sm text-ink-2">{t("guides.whyTheScaleLies.disclaimer")}</p>
          </article>
        </main>
      </div>
    </PageEntrance>
  )
}
